import Marionette from "backbone.marionette";
import Radio from "backbone.radio";

import DebugPanel from "../views/debug";
import AccountModel from "../models/account";
import RoleCollection from "../collections/account-roles";

import environment from "../common/environment";
import store from "../utils/webstorage";
import { CONSTANTS } from "../config/webstorage";

const globalChannel = Radio.channel("global");
const reportChannel = Radio.channel("report");

const CUSTOM_ROLES = CONSTANTS.CUSTOM_ROLES;
const ADMIN_ROLE = "ROLE_ADMIN";
const DEBUG_ROLE = "ROLE_ALTEGRA_STAFF";

class UserProfile extends Marionette.Object {
  initialize() {
    // assume user cannot debug
    this.canDebug = false;

    // setup model
    this.model = new AccountModel();
    // move on while promise is fulfilled
    this.model.promise.done(this._onLoad.bind(this));
    this.model.promise.fail(this._onLoadFailure.bind(this));

    // setup listeners on global and report channels
    globalChannel.reply("user:profile", this._getProfile, this);
    globalChannel.reply("user:id", this._getUserId, this);
    globalChannel.reply("user:name", this._getFullName, this);
    globalChannel.reply(
      "user:abbreviated-name",
      this._getAbbreviatedName,
      this
    );
    globalChannel.reply("user:has-role", this._validateRole, this);
    globalChannel.reply("user:has-roles", this._validateRoles, this);
    globalChannel.reply("user:has-any-role", this._hasAnyRole, this);
    globalChannel.reply("user:has-all-roles", this._hasAllRoles, this);
    globalChannel.reply("user:has-custom-roles", this._hasCustomRoles, this);
    globalChannel.reply("user:roles", this._getRoles, this);
    globalChannel.reply("user:role", this._getRole, this);
    globalChannel.reply(
      "user:remove-custom-roles",
      this._removeCustomRoles,
      this
    );
    globalChannel.reply("user:can-debug", this._canDebug, this);

    reportChannel.reply("user:profile", this._getProfile, this);
    reportChannel.reply("user:id", this._getUserId, this);
    reportChannel.reply("user:name", this._getFullName, this);
    reportChannel.reply(
      "user:abbreviated-name",
      this._getAbbreviatedName,
      this
    );
    reportChannel.reply("user:has-role", this._validateRole, this);
    reportChannel.reply("user:has-roles", this._validateRoles, this);
    reportChannel.reply("user:has-any-role", this._hasAnyRole, this);
    reportChannel.reply("user:has-all-roles", this._hasAllRoles, this);
    reportChannel.reply("user:has-custom-roles", this._hasCustomRoles, this);
    reportChannel.reply("user:roles", this._getRoles, this);
    reportChannel.reply("user:role", this._getRole, this);
    reportChannel.reply(
      "user:remove-custom-roles",
      this._removeCustomRoles,
      this
    );
    reportChannel.reply("user:can-debug", this._canDebug, this);

    this.listenTo(
      globalChannel,
      "user:remove-custom-roles",
      this._removeCustomRoles
    );
  }

  _onLoad() {
    // update custom roles
    this._setCustomRoles();

    // update debug flag, headers, etc.
    this._setDebugMode();

    // notify interested parties
    globalChannel.trigger("user-profile-loaded", this.model);
  }

  _onLoadFailure(xhr) {
    console.warn("[%s] Unable to load user profile.", xhr.status);

    // show error
    globalChannel.trigger("status:show", {
      type: "danger",
      message:
        "We were unable to load your user profile. Please notify your administrator.",
    });
  }

  _setDebugMode() {
    // set debug flag
    this.canDebug = this._hasAnyRole([DEBUG_ROLE, ADMIN_ROLE]);

    if (this.canDebug) {
      // add debug header to jQuery configuration when needed
      jQuery.ajaxSetup({
        headers: {
          Debug: Date.now(),
        },
        // listens for debug messages in any Ajax response
        complete: this._debugFilter,
      });

      // instantiate debug panel
      new DebugPanel();
    }
  }

  // converts list of role IDs to list of keys and sets Ajax header
  // when appropriate.
  _setCustomRoles() {
    let customRoleIds;

    // expect an array of numbers
    try {
      // retrieve custom roles from LS
      customRoleIds = this._getCustomRoles() || [];
    } catch (e) {
      // assume empty array
      customRoleIds = [];
    }

    // determine role keys when found
    if (customRoleIds.length) {
      const roleKeys = customRoleIds
        .map((id) => {
          const role = this._getRole(id);

          // mark role checked on collection
          if (role) {
            this._markRoleChecked(id);
          }

          return role ? role.getKey() : false;
        })
        // remove false values
        .filter((id) => id);

      // do we have a list of role keys and it's not the complete list of
      // available roles?
      if (roleKeys.length && !this._allRolesChecked()) {
        // add ajax header
        jQuery.ajaxSetup({
          headers: {
            // join in comma separated string
            Role: roleKeys.join(),
          },
        });

        // remove roles not in list from local copy of account
        const roleCollection = new RoleCollection();

        roleKeys.forEach((roleId) => {
          roleCollection.add(this._getRole(roleId));
        });

        this._setRoles(roleCollection);
      }
    } else {
      // remove header
      delete jQuery.ajaxSettings.headers["Role"];
    }
  }

  _getProfile() {
    return this.model;
  }

  _getUserId() {
    return this.model.getId();
  }

  _getFullName() {
    return this.model.getFullName();
  }

  _getAbbreviatedName() {
    return this.model.getAbbreviatedName();
  }

  /**
   * Determines whether the user possesses ALL the passed roles.
   *
   * @param {Array} roles
   */
  _hasAllRoles(roles = []) {
    // validate all passed roles
    const _roles = this._validateRoles(roles);

    // are all roles valid?
    return _roles.length === roles.length;
  }

  /**
   * Determines whether the user possesses ANY of the passed roles.
   *
   * @param {Array} roles
   */
  _hasAnyRole(roles = []) {
    // validate all passed roles
    const _roles = this._validateRoles(roles);

    // is any role valid
    return !!_roles.length;
  }

  /**
   * Validates a list of [String] roles.
   * Returns a list of valid roles.
   *
   * @param {Array} roles
   */
  _validateRoles(roles = []) {
    return (
      roles
        // validate roles
        .map((role) => {
          return { name: role, valid: this._validateRole(role) };
        })
        // filter valid roles
        .filter((role) => role.valid)
        // return their names
        .map((role) => role.name)
    );
  }

  /**
   * Validates an individual role.
   *
   * @param {String} role
   */
  _validateRole(role) {
    if (_.isString(role)) {
      return this.model.hasRole(role.toUpperCase());
    } else {
      return false;
    }
  }

  _hasCustomRoles() {
    return !!this._getCustomRoles();
  }

  // updates an account-roles collection
  _setRoles(coll) {
    this.model.setRoles(coll);
  }

  // returns an account-roles collection
  _getRoles() {
    return this.model.getRoles();
  }

  // returns an account-role model by name (String) or id (Number)
  _getRole(role) {
    if (_.isString(role)) {
      return this.model.getRole(role.toUpperCase());
    } else if (_.isNumber(role)) {
      return this.model.getRoleById(role);
    } else {
      return false;
    }
  }

  _getCustomRoles() {
    return store.getValue(CUSTOM_ROLES);
  }

  _removeCustomRoles() {
    store.remove(CUSTOM_ROLES);

    // show message
    globalChannel.trigger("status:show", {
      autoDismissTimeout: 2500,
      type: "success",
      message: "Custom roles have been removed. Reloading the application...",
    });

    // redirect home
    _.delay(() => {
      document.location.assign(`${environment.getAppHost()}/`);
    }, 3000);
  }

  _markRoleChecked(id) {
    this._getRole(id).setChecked(true);
  }

  _allRolesChecked() {
    return this._getRoles().where({ checked: false }).length === 0;
  }

  _canDebug() {
    return this.canDebug;
  }

  // listens to all ajax requests and attempts to pus debug details into the
  // debug panel view. This should only be used by users with the approriate
  // debugging privileges.
  _debugFilter(jqXHR, textStatus) {
    if (
      jqXHR.responseJSON &&
      ("success" === textStatus || "error" === textStatus)
    ) {
      const json = jqXHR.responseJSON;
      // notify debug panel
      try {
        reportChannel.trigger("debug:data", json.meta.debug);
      } catch (e) {
        // do nothing, meta.debug attribute was not sent by the server
      }
    }
  }
}

export { UserProfile as default };
